import { createTheme } from '@mui/material/styles';
import {
    default as loginLogo,
    default as logo,
} from '../../../assets/img/JYRNEY-Logo-WhiteOnClear-Horiz.png';
import favicon from '../../../assets/img/Jyrney/favicon.ico';
import miniBookingLogo from '../../../assets/img/Jyrney/yourTMClogo.png';
import loginBackgroundImage from '../../../assets/img/login.jpeg';
import sidebarImage from '../../../assets/img/sidebar-road1.jpg';
import { grayColor } from '../../../materialUI/jss/material-dashboard-pro-react';

/* =========================================================================
   Jyrney Design System — token values mirrored from styles/tokens.css.
   Any component can consume these via theme.tokens OR via the CSS
   custom properties defined in app/styles/tokens.css (e.g. var(--jy-navy-900)).
   Source of truth: design_handoff_jyrney/styles/tokens.css.
   ========================================================================= */
const tokens = {
  navy: {
    900: '#00253d',
    800: '#0a2f4a',
    700: '#173e5b',
    600: '#2a5273',
    500: '#486e8d',
    400: '#7a95ac',
    300: '#a9bcca',
    200: '#d0dbe4',
    100: '#eaf0f4',
    50: '#f5f8fa',
  },
  amber: {
    700: '#c57d1f',
    600: '#e89530',
    500: '#f4a94a',
    400: '#ffbf6e',
    300: '#ffd49a',
    200: '#ffe7c3',
    100: '#fff3e0',
  },
  ink: {
    900: '#0d1418',
    800: '#212a31',
    700: '#3b464f',
    600: '#5a6670',
    500: '#808b95',
    400: '#a5afb8',
    300: '#c9d0d6',
    200: '#e2e6ea',
    100: '#eff1f4',
    50: '#f7f8fa',
  },
  status: {
    success: '#167a4e',
    successBg: '#e6f4ec',
    successVivid: '#138a52',
    warning: '#9a5f14',
    warningBg: '#fff3e0',
    warningVivid: '#f59e0b',
    danger: '#b23125',
    dangerBg: '#fbeae8',
    dangerVivid: '#d32f2f',
    info: '#155b95',
    infoBg: '#e4f0fa',
    infoVivid: '#1976d2',
  },
  radius: {
    xs: 4,
    sm: 6,
    md: 10,
    lg: 14,
    xl: 20,
    '2xl': 28,
    pill: 999,
  },
  shadow: {
    xs: '0 1px 2px rgba(10, 37, 61, 0.06)',
    sm: '0 2px 6px rgba(10, 37, 61, 0.08)',
    md: '0 6px 18px rgba(10, 37, 61, 0.10)',
    lg: '0 16px 40px rgba(10, 37, 61, 0.14)',
    xl: '0 32px 64px rgba(10, 37, 61, 0.18)',
  },
  motion: {
    easeOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.6, 0, 0.4, 1)',
    durFast: '140ms',
    durBase: '220ms',
    durSlow: '360ms',
  },
  font: {
    display: '"Montserrat", Arial, sans-serif',
    sans: '"Montserrat", Arial, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  tracking: {
    hero: '0.02em',
    caps: '0.14em',
    tight: '-0.01em',
  },
  navHeight: 72,
}

let theme = createTheme({
  colorBg: '#e8e8e8',
  tokens,
  brand: {
    name: '',
    logo: logo,
    sidebarImage: sidebarImage,
    miniBookingLogo: miniBookingLogo,
    browserTabName: 'Jyrney',
    favicon: favicon,
  },
  shape: { borderRadius: tokens.radius.lg },
  typography: {
    fontFamily: tokens.font.sans,
    fontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    h1: {
      fontFamily: tokens.font.display,
      fontWeight: 700,
      fontSize: 'clamp(2rem, 3.5vw, 3.5rem)',
      lineHeight: 1.2,
      letterSpacing: tokens.tracking.hero,
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: tokens.tracking.hero,
      textTransform: 'uppercase',
    },
    h3: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.2,
      letterSpacing: tokens.tracking.tight,
    },
    h4: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.2,
      letterSpacing: tokens.tracking.tight,
    },
    h5: {
      fontFamily: tokens.font.display,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.45,
    },
    body1: {
      fontFamily: tokens.font.sans,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: tokens.font.sans,
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: tokens.font.sans,
      fontWeight: 700,
      letterSpacing: tokens.tracking.caps,
      textTransform: 'uppercase',
    },
    caption: {
      fontFamily: tokens.font.sans,
      fontSize: '0.75rem',
      letterSpacing: tokens.tracking.caps,
      textTransform: 'uppercase',
      fontWeight: 600,
    },
  },
  map: {
    routeColor: tokens.navy[900],
    routeColorSatellite: '#ffd400',
  },
  palette: {
    brand: {
      dark: tokens.navy[900],
      main: tokens.navy[900],
      light: tokens.navy[500],
    },
    primary: {
      main: tokens.navy[900],
      light: tokens.navy[700],
      dark: tokens.navy[900],
      contrastText: '#ffffff',
    },
    secondary: {
      main: tokens.amber[500],
      light: tokens.amber[300],
      dark: tokens.amber[700],
      contrastText: tokens.navy[900],
    },
    info: {
      main: tokens.status.info,
      light: tokens.status.infoBg,
      dark: '#0e3f68',
      contrastText: '#ffffff',
    },
    success: {
      main: tokens.status.success,
      light: tokens.status.successBg,
      dark: '#0f5838',
      contrastText: '#ffffff',
    },
    warning: {
      main: tokens.status.warning,
      light: tokens.status.warningBg,
      dark: '#6d430e',
      contrastText: '#ffffff',
    },
    error: {
      main: tokens.status.danger,
      light: tokens.status.dangerBg,
      dark: '#7d221a',
      contrastText: '#ffffff',
    },
    white: {
      main: '#FFFFFF',
      light: '#FFFFFF',
      dark: '#cccccc',
      contrastText: grayColor[0],
    },
    rose: {
      50: '#EC407A',
      100: '#EB3573',
      200: '#E91E63',
      300: '#D81B60',
      400: '#C2185B',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: tokens.ink[900],
      secondary: tokens.ink[600],
      disabled: tokens.ink[400],
    },
    divider: tokens.ink[200],
    common: {
      black: '#000',
      white: '#FFF',
    },
  },
})

theme = createTheme(theme, {
  pages: {
    loginPage: {
      backgroundImage: 'url(' + loginBackgroundImage + ')',
      titleHeaderInnerColor: theme.palette.secondary.main,
      titleHeaderOuterColor: theme.palette.primary.main,
      buttonColor: theme.palette.secondary.main,
      loginLogo: loginLogo,
    },
  },
})

theme = createTheme(theme, {
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    Table: {
      headerColor: theme.palette.brand.light,
      gridBackground: theme.palette.white.main,
    },
    MuiCssBaseline: {
      styleOverrides: {
        ':focus-visible': {
          outline: `2px solid ${tokens.amber[500]}`,
          outlineOffset: 3,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          minHeight: 48,
          minWidth: 'auto',
          padding: '11.2px 17.6px',
          fontFamily: tokens.font.sans,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: tokens.tracking.caps,
          textTransform: 'uppercase',
          borderRadius: tokens.radius.sm,
          backgroundColor: tokens.amber[500],
          color: tokens.navy[900],
          border: '1px solid transparent',
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          transition: `background-color ${tokens.motion.durFast} ${tokens.motion.easeOut}, border-color ${tokens.motion.durFast} ${tokens.motion.easeOut}, transform ${tokens.motion.durFast} ${tokens.motion.easeOut}`,
          '&:hover': {
            backgroundColor: tokens.amber[600],
            boxShadow: 'none',
          },
          '&:active': { transform: 'translateY(1px)' },
          '&:focus-visible': {
            outline: `2px solid ${tokens.amber[500]}`,
            outlineOffset: 3,
          },
          '& .fab,& .fas,& .far,& .fal,& .material-icons': {
            position: 'relative',
            display: 'inline-block',
            top: 0,
            fontSize: '1.1rem',
            marginRight: 4,
            verticalAlign: 'middle',
          },
          '& svg': {
            position: 'relative',
            display: 'inline-block',
            top: 0,
            width: 18,
            height: 18,
            marginRight: 4,
            verticalAlign: 'middle',
          },

          /* ---- Variants driven by legacy Button wrapper props ---- */
          ...(ownerState?.simple && {
            '&,&:focus,&:hover': {
              background: 'transparent',
              color: tokens.navy[900],
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.simple &&
            ownerState?.color && {
              color: theme.palette[ownerState?.color]?.main || tokens.navy[900],
            }),
          ...(ownerState?.transparent && {
            '&,&:focus,&:hover': {
              color: 'inherit',
              background: 'transparent',
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.disabled && {
            opacity: 0.45,
            pointerEvents: 'none',
          }),
          ...(ownerState?.outlined && {
            border: `1px solid ${tokens.navy[900]}`,
            background: 'transparent',
            color: tokens.navy[900],
            '&:focus,&:hover': {
              color: '#fff',
              background: tokens.navy[900],
            },
          }),
          ...(ownerState?.text && {
            color: tokens.navy[900],
            fontWeight: 700,
            backgroundColor: 'transparent',
            border: 'none',
            minHeight: 'auto',
            padding: 5,
            margin: 0,
            '&:focus,&:hover': {
              backgroundColor: 'transparent',
            },
            opacity: ownerState.disabled ? 0.5 : 1,
          }),

          /* ---- Color variants ---- */
          ...(ownerState?.color === 'primary' && {
            backgroundColor: tokens.navy[900],
            color: '#fff',
            '&:hover': { backgroundColor: tokens.navy[800] },
          }),
          ...(ownerState?.color === 'secondary' && {
            backgroundColor: tokens.amber[500],
            color: tokens.navy[900],
            '&:hover': { backgroundColor: tokens.amber[600] },
          }),
          ...(ownerState?.color === 'error' && {
            backgroundColor: tokens.status.danger,
            color: '#fff',
            '&:hover': { backgroundColor: '#7d221a' },
          }),
          ...(ownerState?.color === 'success' && {
            backgroundColor: tokens.status.success,
            color: '#fff',
            '&:hover': { backgroundColor: '#0f5838' },
          }),
          ...(ownerState?.color === 'warning' && {
            backgroundColor: tokens.status.warning,
            color: '#fff',
            '&:hover': { backgroundColor: '#6d430e' },
          }),
          ...(ownerState?.color === 'info' && {
            backgroundColor: tokens.status.info,
            color: '#fff',
            '&:hover': { backgroundColor: '#0e3f68' },
          }),
          ...(ownerState?.color === 'white' && {
            backgroundColor: '#fff',
            color: tokens.navy[900],
            border: `1px solid ${tokens.ink[200]}`,
            '&:hover': { backgroundColor: tokens.ink[100] },
          }),

          /* Legacy jyrneyYellow/Blue/Red kept as safe no-ops mapped to
             the new accent/primary/danger so older call-sites don't
             render as black-on-grey fallback. */
          ...(ownerState?.color === 'jyrneyYellow' && {
            backgroundColor: tokens.amber[500],
            color: tokens.navy[900],
            '&:hover': { backgroundColor: tokens.amber[600] },
          }),
          ...(ownerState?.color === 'jyrneyBlue' && {
            backgroundColor: tokens.navy[900],
            color: '#fff',
            '&:hover': { backgroundColor: tokens.navy[800] },
          }),
          ...(ownerState?.color === 'jyrneyRed' && {
            backgroundColor: tokens.status.danger,
            color: '#fff',
            '&:hover': { backgroundColor: '#7d221a' },
          }),

          /* Sizes */
          ...(ownerState?.size === 'sm' && {
            minHeight: 36,
            padding: '6.4px 11.2px',
            fontSize: 12,
          }),
          ...(ownerState?.size === 'lg' && {
            minHeight: 56,
            padding: '14.4px 22.4px',
            fontSize: 15,
          }),
          ...(ownerState?.round && {
            borderRadius: tokens.radius.pill,
          }),
          ...(ownerState?.justicon && {
            paddingLeft: 12,
            paddingRight: 12,
            fontSize: 20,
            height: 41,
            minWidth: 41,
            width: 41,
            '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
              marginRight: 0,
            },
          }),
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderRadius: tokens.radius.md,
          fontSize: 16,
          transition: `border-color ${tokens.motion.durFast} ${tokens.motion.easeOut}, box-shadow ${tokens.motion.durFast} ${tokens.motion.easeOut}`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ink[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ink[500],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.navy[900],
            borderWidth: 1,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px rgba(244, 169, 74, 0.25)`,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.ink[600],
          '&.Mui-focused': { color: tokens.navy[900] },
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: {
          color: tokens.navy[900],
          '&:hover': { color: tokens.amber[600] },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: { margin: 0, padding: 0 },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: { padding: 0, margin: 0 },
      },
    },
    MuiIcon: {
      styleOverrides: {
        root: { margin: 0, padding: 0 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: tokens.font.sans,
          fontWeight: 700,
          letterSpacing: '0.04em',
          borderRadius: tokens.radius.pill,
          '& .MuiChip-icon': { color: 'inherit' },
        },
        colorSuccess: {
          backgroundColor: tokens.status.successVivid,
          color: '#ffffff',
        },
        colorWarning: {
          backgroundColor: tokens.status.warningVivid,
          color: tokens.navy[900],
        },
        colorError: {
          backgroundColor: tokens.status.dangerVivid,
          color: '#ffffff',
        },
        colorInfo: {
          backgroundColor: tokens.status.infoVivid,
          color: '#ffffff',
        },
        colorPrimary: {
          backgroundColor: tokens.navy[600],
          color: '#ffffff',
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          fontFamily: tokens.font.sans,
          fontWeight: 600,
          borderRadius: tokens.radius.md,
          '& .MuiAlert-icon': { color: 'inherit' },
        },
        filledSuccess: {
          backgroundColor: tokens.status.successVivid,
          color: '#ffffff',
        },
        filledWarning: {
          backgroundColor: tokens.status.warningVivid,
          color: tokens.navy[900],
        },
        filledError: {
          backgroundColor: tokens.status.dangerVivid,
          color: '#ffffff',
        },
        filledInfo: {
          backgroundColor: tokens.status.infoVivid,
          color: '#ffffff',
        },
        standardSuccess: {
          backgroundColor: tokens.status.successBg,
          color: tokens.status.successVivid,
        },
        standardWarning: {
          backgroundColor: tokens.status.warningBg,
          color: tokens.status.warning,
        },
        standardError: {
          backgroundColor: tokens.status.dangerBg,
          color: tokens.status.dangerVivid,
        },
        standardInfo: {
          backgroundColor: tokens.status.infoBg,
          color: tokens.status.infoVivid,
        },
      },
    },
    Sidebar: {
      styleOverrides: {
        overlay: {
          background: '#00253DE5',
        },
      },
    },
    CardHeader: {
      styleOverrides: {
        root: (ownerState, theme) => {
          const color = ownerState?.color || 'white'
          const background =
            color === 'white'
              ? theme.palette.white.main
              : theme.palette[color]?.main || theme.palette.primary.main
          return {
            background,
            boxShadow: 'none',
          }
        },
      },
    },
    CardIcon: {
      styleOverrides: {
        root: (ownerState, theme) => {
          const color = ownerState?.color || 'white'
          return {
            background:
              color === 'white'
                ? theme.palette.white.main
                : theme.palette[color]?.main || theme.palette.primary.main,
            boxShadow: 'none',
          }
        },
      },
    },
    bookingSummary: {
      backgroundImage: theme.palette.brand.main,
      headerColour: theme.palette.brand.dark,
      iconLineComplete: theme.palette.brand.main,
      iconLinePending: tokens.ink[400],
      iconFillComplete: theme.palette.secondary.main,
      iconFillPending: tokens.ink[400],
      sectionTitle: theme.palette.secondary.main,
      sectionAnswerComplete: theme.palette.primary.contrastText,
      sectionAnswerPending: tokens.ink[400],
    },
    journeyStatus: {
      headerColour: theme.palette.brand.dark,
      headerTextColour: theme.palette.white.main,
    },
    BookingTable: {
      headerColour: theme.palette.brand.dark,
    },
  },
  tableComponents: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          textAlign: 'left',
          textTransform: 'none',
          padding: 0,
          justifyContent: 'flex-start',
          minHeight: 'auto',
          background: 'transparent',
          color: tokens.navy[900],
          '&:hover': { background: 'unset' },
        },
      },
    },
  },
})

export default theme
